import React, { useState, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Button,
  TextField,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const AdminAccessManager = forwardRef(
  ({ userEmail, formData, setFormData, advertiserMetaDetails = [], onAdvertiserAdded, currentAccessData = {} }, ref) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [addPlatform, setAddPlatform] = useState('DSP');
    const [selectedAdvertisersWithTimezone, setSelectedAdvertisersWithTimezone] = useState([]);
    const [removedAdvertisers, setRemovedAdvertisers] = useState([]);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      addRemovedAdvertiser: (advertiserName, timezone) => {
        // Add the advertiser back to removed advertisers list so it appears in available list
        setRemovedAdvertisers((prev) => [...prev, { name: advertiserName, timezone }]);
      },
      addRemovedOrganization: (orgName) => {
        // Organizations are handled at a different level, but we can log this
      }
    }));

    // Admin: Get all advertisers from meta details (no timezone - user chooses when adding)
    const getAvailableAdvertisers = () => {
      // Use a Map to ensure uniqueness by advertiser_guid
      const uniqueAdvertisersMap = new Map();

      advertiserMetaDetails.forEach((advertiser) => {
        const advertiserData = {
          name: advertiser.advertiser_name,
          id: advertiser.advertiser_guid,
          sfId: advertiser.salesforce_account_id,
          org_name: advertiser.org_name,
          start_date: advertiser.start_date
        };

        // Primary uniqueness check: Use advertiser_guid as the key
        if (advertiser.advertiser_guid && !uniqueAdvertisersMap.has(advertiser.advertiser_guid)) {
          uniqueAdvertisersMap.set(advertiser.advertiser_guid, advertiserData);
        } else if (!advertiser.advertiser_guid) {
          // Fallback: if no guid, check by name to avoid duplicates
          const existingByName = Array.from(uniqueAdvertisersMap.values()).find((existing) => existing.name === advertiser.advertiser_name);
          if (!existingByName) {
            const tempKey = `temp_${advertiser.advertiser_name}_${Date.now()}`;
            uniqueAdvertisersMap.set(tempKey, advertiserData);
          }
        }
      });

      const allAdvertisers = Array.from(uniqueAdvertisersMap.values());

      // Filter out advertisers that are already in Current Access to prevent duplication
      const currentAccessAdvertisersUTC = currentAccessData.advertisers_utc || [];
      const currentAccessAdvertisersJST = currentAccessData.advertisers_non_utc || [];
      const currentAccessAdvertisersCTV = currentAccessData.advertisers_ctv || [];
      const currentAccessAdvertisersGTV = currentAccessData.advertisers_gtv || [];

      const filteredMainAdvertisers = allAdvertisers.filter((advertiser) => {
        const inUtc = currentAccessAdvertisersUTC.some((adv) => (adv.id || adv.name) === advertiser.id);
        const inJst = currentAccessAdvertisersJST.some((adv) => (adv.id || adv.name) === advertiser.id);
        const inCtv = currentAccessAdvertisersCTV.some((adv) => (adv.id || adv.name) === advertiser.id);
        const inGtv = currentAccessAdvertisersGTV.some((adv) => (adv.id || adv.name) === advertiser.id);
        if (addPlatform === 'CTV') {
          return !inCtv;
        }
        if (addPlatform === 'GTV') {
          return !inGtv;
        }
        return !inUtc && !inJst;
      });

      // Then, add removed advertisers only if they don't already exist in the main list
      const availableAdvertisers = [...filteredMainAdvertisers];

      removedAdvertisers.forEach((removedAdv) => {
        // Check if this removed advertiser already exists in the main list by guid or name
        const alreadyExists = availableAdvertisers.some(
          (existing) =>
            existing.name === removedAdv.name ||
            existing.id === removedAdv.name ||
            existing.advertiser_guid === removedAdv.name ||
            (existing.sfId && existing.sfId === removedAdv.name)
        );

        if (!alreadyExists) {
          availableAdvertisers.push({
            name: removedAdv.name,
            id: removedAdv.name,
            sfId: '',
            org_name: ''
          });
        }
      });

      return availableAdvertisers;
    };

    const availableAdvertisers = getAvailableAdvertisers();

    // Handle advertiser selection with timezone
    const handleTimezoneSelect = (advertiser, timezone, platform = 'DSP') => {
      // Add to selected advertisers
      setSelectedAdvertisersWithTimezone((prev) => [...prev, { ...advertiser, timezone, platform }]);

      // Update form data
      const currentAdvertisers = formData.advertiserAccess
        ? formData.advertiserAccess.split(', ').filter((name) => name.trim() !== '')
        : [];
      const updatedAdvertisers = [...currentAdvertisers, advertiser.name];

      setFormData((prev) => ({
        ...prev,
        advertiserAccess: updatedAdvertisers.join(', ')
      }));

      // Remove from removed advertisers list since it's now added back
      setRemovedAdvertisers((prev) => prev.filter((adv) => adv.name !== advertiser.name));

      // Notify parent component about the new advertiser
      if (onAdvertiserAdded) {
        onAdvertiserAdded(advertiser, timezone, platform);
      }
    };

    // Handle removing existing advertisers
    const handleRemoveExistingAdvertiser = (advertiserName, timezone) => {
      // Remove from selected advertisers
      setSelectedAdvertisersWithTimezone((prev) => prev.filter((adv) => !(adv.name === advertiserName && adv.timezone === timezone)));

      // Update form data
      const currentAdvertisers = formData.advertiserAccess
        ? formData.advertiserAccess.split(', ').filter((name) => name.trim() !== '')
        : [];
      const updatedAdvertisers = currentAdvertisers.filter((name) => name !== advertiserName);

      setFormData((prev) => ({
        ...prev,
        advertiserAccess: updatedAdvertisers.join(', ')
      }));

      // Add back to available list (admin always adds back)
      setRemovedAdvertisers((prev) => [...prev, { name: advertiserName, timezone }]);
    };

    // Filter available advertisers based on search
    const filteredAvailableAdvertisers = useMemo(() => {
      if (!searchTerm.trim()) {
        return availableAdvertisers;
      }

      const searchLower = searchTerm.toLowerCase();
      return availableAdvertisers.filter(
        (advertiser) =>
          advertiser.name.toLowerCase().includes(searchLower) || (advertiser.sfId && advertiser.sfId.toLowerCase().includes(searchLower))
      );
    }, [availableAdvertisers, searchTerm, addPlatform]);

    return (
      <Grid item xs={12}>
        {/* Available Advertisers Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" color="textPrimary" sx={{ mb: 2 }}>
            Available Advertisers - Select platform, then timezone when adding
          </Typography>

          <Box sx={{ mb: 2, maxWidth: 280 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="admin-access-platform-label">Platform</InputLabel>
              <Select
                labelId="admin-access-platform-label"
                label="Platform"
                value={addPlatform}
                onChange={(e) => setAddPlatform(e.target.value)}
              >
                <MenuItem value="DSP">DSP</MenuItem>
                <MenuItem value="CTV">CTV</MenuItem>
                <MenuItem value="GTV">GTV</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Search Bar */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search advertisers by name or SF ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ maxWidth: 400 }}
            />
          </Box>

          {/* Advertisers Table */}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 2,
              overflow: 'hidden',
              maxHeight: '400px',
              overflowY: 'auto'
            }}
          >
            {/* Table Header */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1fr',
                gap: 2,
                p: 2,
                bgcolor: 'grey.100',
                borderBottom: '1px solid',
                borderColor: 'grey.300',
                fontWeight: 'bold'
              }}
            >
              <Typography variant="subtitle2">Advertiser Name</Typography>
              <Typography variant="subtitle2">SF ID</Typography>
              <Typography variant="subtitle2">ORG</Typography>
              <Typography variant="subtitle2">Action</Typography>
            </Box>

            {/* Table Body */}
            {filteredAvailableAdvertisers.length > 0 ? (
              filteredAvailableAdvertisers.map((advertiser, index) => (
                <Box
                  key={`${advertiser.name}-${index}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr 1fr',
                    gap: 2,
                    p: 2,
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                    alignItems: 'center',
                    '&:hover': {
                      bgcolor: 'grey.50'
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: '500' }}>
                    {advertiser.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {advertiser.sfId || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {advertiser.org_name || 'N/A'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleTimezoneSelect(advertiser, 'UTC', addPlatform)}
                      sx={{
                        borderColor: '#1976d2',
                        color: '#1976d2',
                        fontSize: '11px',
                        fontWeight: '500',
                        '&:hover': {
                          backgroundColor: '#1976d2',
                          color: 'white'
                        }
                      }}
                    >
                      {addPlatform === 'CTV' ? 'Add to CTV (UTC)' : addPlatform === 'GTV' ? 'Add to GTV (UTC)' : 'Add to DSP UTC'}
                    </Button>
                    {addPlatform === 'DSP' && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleTimezoneSelect(advertiser, 'JST', 'DSP')}
                        sx={{
                          borderColor: '#2e7d32',
                          color: '#2e7d32',
                          fontSize: '11px',
                          fontWeight: '500',
                          '&:hover': {
                            backgroundColor: '#2e7d32',
                            color: 'white'
                          }
                        }}
                      >
                        Add to DSP JST
                      </Button>
                    )}
                  </Box>
                </Box>
              ))
            ) : (
              <Box
                sx={{
                  p: 3,
                  textAlign: 'center',
                  color: 'text.secondary',
                  borderTop: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <Typography variant="body2">
                  {searchTerm.trim() ? 'No advertisers found matching your search.' : 'No available advertisers.'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Empty State */}
        {availableAdvertisers.length === 0 && (
          <Box
            sx={{
              p: 3,
              bgcolor: 'success.light',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'success.main',
              textAlign: 'center'
            }}
          >
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: '2rem', mb: 1 }} />
            <Typography variant="body1" color="success.dark">
              {formData.email} already has access to all available advertisers
            </Typography>
          </Box>
        )}

        {/* No Advertisers Found */}
        {advertiserMetaDetails.length === 0 && (
          <Box
            sx={{
              p: 3,
              bgcolor: 'error.light',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'error.main',
              textAlign: 'center'
            }}
          >
            <Typography variant="body1" color="error.dark">
              No advertisers found. Please contact an administrator.
            </Typography>
          </Box>
        )}
      </Grid>
    );
  }
);

AdminAccessManager.propTypes = {
  userEmail: PropTypes.string.isRequired,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  advertiserMetaDetails: PropTypes.array,
  onAdvertiserAdded: PropTypes.func,
  currentAccessData: PropTypes.object
};

AdminAccessManager.defaultProps = {
  advertiserMetaDetails: [],
  currentAccessData: {}
};

export default AdminAccessManager;
